"use client";

import { useMarket } from "@context/marketContext";
import type {
  BrandPageFragment,
  CampaignFragment,
  CategoryPageFragment,
  DesignerPageFragment,
} from "@server/gql/graphql";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import type { Product, Variant } from "types";

import { ProductCardContext } from "./ProductCardContext";

type ProductCardRootProps = {
  children?: ReactNode;
  product?: Product | Variant;
  pageData?:
    | CampaignFragment
    | BrandPageFragment
    | CategoryPageFragment
    | DesignerPageFragment
    | null;
  hideHoverImage?: boolean;
  hideStock?: boolean;
  isDense?: boolean;
  isEager?: boolean;
  onRemove?: () => void;
};

const ProductCardRoot = ({
  children,
  product,
  pageData,
  hideHoverImage,
  hideStock,
  isDense,
  isEager,
  onRemove,
}: ProductCardRootProps) => {
  const marketConfig = useMarket().state.market;
  const [hoverImageHasLoaded, setHoverImageHasLoaded] = useState(false);
  const primaryImage = product?.images?.at(0);
  const hoverImage = product?.images?.at(1);

  const productCardContextValue = useMemo(
    () => ({
      marketConfig,
      locale: marketConfig.locale,
      pageData,
      product,
      primaryImage,
      hoverImage,
      hoverImageHasLoaded,
      setHoverImageHasLoaded,
      isDense,
      hideHoverImage,
      hideStock,
      isEager,
      onRemove,
    }),
    [
      marketConfig,
      pageData,
      product,
      primaryImage,
      hoverImage,
      hoverImageHasLoaded,
      setHoverImageHasLoaded,
      isDense,
      hideHoverImage,
      hideStock,
      isEager,
      onRemove,
    ],
  );

  if (!product) return null;

  return (
    <ProductCardContext.Provider value={productCardContextValue}>
      {children}
    </ProductCardContext.Provider>
  );
};

ProductCardRoot.displayName = "ProductCardRoot";

export default ProductCardRoot;
